import React from "react";
import ReactPlayer from "react-player/lazy";
import styled from "styled-components";
import { HistoryData, PastEditionVideos } from "../data/HistoryData";

const Section = styled.section`
  background: #ffffff;
  padding: 5rem 1.5rem;
`;

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Eyebrow = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.85rem;
  color: #faa71e;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Heading = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  color: #0a2a4a;
  font-weight: 800;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Sub = styled.p`
  text-align: center;
  color: #2d3e56;
  margin-bottom: 3rem;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const Timeline = styled.ol`
  position: relative;
  list-style: none;
  padding: 0;
  margin: 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 110px;
    width: 3px;
    background: linear-gradient(180deg, #ffce2d, #faa71e);
    border-radius: 3px;

    @media screen and (max-width: 720px) {
      left: 22px;
    }
  }
`;

const Item = styled.li`
  position: relative;
  padding: 1rem 0 2.5rem 150px;
  min-height: 64px;

  @media screen and (max-width: 720px) {
    padding-left: 56px;
  }

  &::after {
    content: "";
    position: absolute;
    left: 102px;
    top: 1.4rem;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #faa71e;
    border: 4px solid #fff;
    box-shadow: 0 0 0 3px rgba(250, 167, 30, 0.25);

    @media screen and (max-width: 720px) {
      left: 14px;
    }
  }
`;

const Year = styled.span`
  position: absolute;
  left: 0;
  top: 1rem;
  font-weight: 800;
  font-size: 1.5rem;
  color: #0a2a4a;
  width: 90px;
  text-align: right;

  @media screen and (max-width: 720px) {
    position: static;
    display: block;
    width: auto;
    text-align: left;
    margin-bottom: 0.25rem;
    color: #faa71e;
    font-size: 1.25rem;
  }
`;

const Title = styled.h3`
  color: #0a2a4a;
  font-size: 1.2rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
`;

const Text = styled.p`
  color: #2d3e56;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const Photo = styled.a`
  display: block;
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 14px;
  overflow: hidden;
  background: #fffcf2;
  box-shadow: 0 12px 28px -16px rgba(10, 42, 74, 0.3);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 36px -16px rgba(10, 42, 74, 0.4);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  figcaption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0.6rem 0.85rem;
    background: linear-gradient(180deg, transparent 0%, rgba(10, 42, 74, 0.85) 100%);
    color: #fff;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.3;
  }
`;

const VideosHeading = styled.h3`
  margin-top: 4rem;
  margin-bottom: 0.5rem;
  font-size: clamp(1.4rem, 3vw, 1.8rem);
  color: #0a2a4a;
  font-weight: 800;
  text-align: center;
`;

const VideosSub = styled.p`
  color: #2d3e56;
  text-align: center;
  margin-bottom: 2rem;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const VideoCard = styled.div`
  background: #fffcf2;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 30px -10px rgba(10, 42, 74, 0.18);
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-4px);
  }

  .player {
    aspect-ratio: 16 / 9;
    background: #000;
  }

  .meta {
    padding: 1rem 1.25rem;
  }

  h4 {
    color: #0a2a4a;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }

  span {
    color: #faa71e;
    font-weight: 700;
    font-size: 0.85rem;
    letter-spacing: 0.06em;
  }
`;

const sortedHistory = [...HistoryData].reverse();

const HistoryTimeline = () => {
  return (
    <Section id="istoric">
      <Wrap>
        <Eyebrow>Din istoricul cursei</Eyebrow>
        <Heading>{HistoryData.length} ediții, sute de copii ajutați</Heading>
        <Sub>
          Fondurile strânse de-a lungul anilor au sprijinit spitale, școli și
          centre din Timișoara și județul Timiș. Iată ce am reușit împreună.
        </Sub>

        <Timeline>
          {sortedHistory.map((item) => (
            <Item key={item.year} data-aos="fade-up" data-aos-duration="500">
              <Year>{item.year}</Year>
              <Title>{item.title}</Title>
              <Text>{item.text}</Text>
              {item.images && item.images.length > 0 && (
                <Gallery>
                  {item.images.map((img, i) => (
                    <Photo
                      key={i}
                      href={img.src}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src={img.src} alt={img.alt} loading="lazy" />
                      <figcaption>{img.alt}</figcaption>
                    </Photo>
                  ))}
                </Gallery>
              )}
            </Item>
          ))}
        </Timeline>

        <VideosHeading>Ediții pe peliculă</VideosHeading>
        <VideosSub>Filmările oficiale ale curselor din anii precedenți.</VideosSub>
        <VideoGrid>
          {PastEditionVideos.map((v) => (
            <VideoCard key={v.url}>
              <div className="player">
                <ReactPlayer
                  url={v.url}
                  width="100%"
                  height="100%"
                  light
                  controls
                />
              </div>
              <div className="meta">
                <span>{v.year}</span>
                <h4>{v.title}</h4>
              </div>
            </VideoCard>
          ))}
        </VideoGrid>
      </Wrap>
    </Section>
  );
};

export default HistoryTimeline;
